import  { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Grid,
  Image,
  Loader,
  FileInput,
  NumberInput,
  SimpleGrid,
  Card,
  ActionIcon,
  Text,
  TagsInput,
  Modal,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import axios from 'axios';
import { useAppSelector } from '../../../../store/hooks';
import { IconTrash, IconEdit, IconUpload } from '@tabler/icons-react';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  tags: string[];
  readTime: string;
  imageUrl: string;
  order: number;
  active: boolean;
}

export const BlogGalleryPage = () => {
  const { token } = useAppSelector((state) => state.auth);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlogPost | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: '',
      date: new Date().toISOString().split('T')[0],
      category: '',
      tags: [] as string[],
      readTime: '',
      order: 0,
      active: true,
    },
  });

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery/BLOG`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const gallery = res.data;
      const items = gallery.items
        .filter((item: any) => item.type === 'IMAGE')
        .map((item: any) => {
          let meta: any = {};
          try { meta = JSON.parse(item.metadata || '{}'); } catch(e) {}
          if (!meta.isBlogPost) return null;
          return {
            id: item.id,
            title: meta.title || item.title,
            slug: meta.slug || '',
            excerpt: meta.excerpt || '',
            content: meta.content || '',
            author: meta.author || '',
            date: meta.date || new Date().toISOString().split('T')[0],
            category: meta.category || '',
            tags: meta.tags || [],
            readTime: meta.readTime || '',
            imageUrl: item.imageUrl,
            order: meta.order || 0,
            active: item.active,
          };
        })
        .filter(Boolean)
        .sort((a: BlogPost, b: BlogPost) => a.order - b.order);
      setPosts(items);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      notifications.show({ title: 'Error', message: 'Failed to load posts', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const resetForm = () => {
    form.reset();
    setImageFile(null);
    setEditingId(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!imageFile && !editingId) {
      notifications.show({ title: 'Error', message: 'Please select a featured image', color: 'red' });
      return;
    }

    setSubmitting(true);
    const slug = values.slug || generateSlug(values.title);
    const formData = new FormData();
    const metadata = {
      isBlogPost: true,
      title: values.title,
      slug,
      excerpt: values.excerpt,
      content: values.content,
      author: values.author,
      date: values.date,
      category: values.category,
      tags: values.tags,
      readTime: values.readTime,
      order: values.order,
    };
    const data = {
      title: values.title,
      description: values.excerpt,
      type: 'IMAGE',
      active: values.active,
      orderIndex: 0,
      metadata: JSON.stringify(metadata),
    };
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob);
    if (imageFile) formData.append('file', imageFile);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/gallery/items/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Post updated', color: 'green' });
      } else {
        await axios.post(`${API_URL}/gallery/BLOG/items`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Post added', color: 'green' });
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Operation failed', color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/gallery/items/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: 'Success', message: 'Post deleted', color: 'green' });
      closeDeleteModal();
      fetchPosts();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Delete failed', color: 'red' });
    } finally {
      setSubmitting(false);
      setDeleteTarget(null);
    }
  };

  const editPost = (post: BlogPost) => {
    setEditingId(post.id);
    form.setValues({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      date: post.date,
      category: post.category,
      tags: post.tags,
      readTime: post.readTime,
      order: post.order,
      active: true,
    });
    setImageFile(null);
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Title order={2} mb="lg">Manage Blog Posts</Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">{editingId ? 'Edit Post' : 'Add New Post'}</Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput label="Title *" placeholder="Post title" {...form.getInputProps('title')} required />
                <TextInput label="Slug" placeholder="auto-generated from title" {...form.getInputProps('slug')} />
                <Textarea label="Excerpt" placeholder="Short summary" {...form.getInputProps('excerpt')} />
                <Textarea label="Content (HTML)" placeholder="Full HTML content" minRows={5} {...form.getInputProps('content')} />
                <TextInput label="Author" placeholder="Author name" {...form.getInputProps('author')} />
                <TextInput label="Date" type="date" {...form.getInputProps('date')} />
                <TextInput label="Category" placeholder="e.g., Market Insights" {...form.getInputProps('category')} />
                <TagsInput label="Tags" placeholder="Add tags" {...form.getInputProps('tags')} />
                <TextInput label="Read Time" placeholder="5 min read" {...form.getInputProps('readTime')} />
                <NumberInput label="Display Order" {...form.getInputProps('order')} />
                <FileInput
                  label="Featured Image"
                  placeholder="Click to upload"
                  accept="image/*"
                  value={imageFile}
                  onChange={setImageFile}
                  leftSection={<IconUpload size={16} />}
                />
                <Group justify="flex-end" mt="md">
                  {editingId && <Button variant="light" onClick={cancelEdit}>Cancel</Button>}
                  <Button type="submit" color="primary" loading={submitting}>{editingId ? 'Update' : 'Create'}</Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">Existing Posts ({posts.length})</Title>
            {posts.length === 0 ? (
              <Text c="dimmed">No posts added yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {posts.map((post) => (
                  <Card key={post.id} withBorder padding="sm">
                    <Card.Section>
                      <Image
                        src={post.imageUrl.startsWith('http') ? post.imageUrl : `${API_URL}${post.imageUrl}`}
                        height={100}
                        fit="cover"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200' viewBox='0 0 400 200'%3E%3Crect width='400' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E"
                      loading="lazy" 
                      />
                    </Card.Section>
                    <Stack mt="xs" gap={5}>
                      <Text fw={600}>{post.title}</Text>
                      <Text size="xs" c="dimmed">{post.author} | {post.date}</Text>
                      <Text size="xs" lineClamp={2}>{post.excerpt}</Text>
                      <Text size="xs">Order: {post.order}</Text>
                      <Group justify="space-between" mt="xs">
                        <ActionIcon color="blue" onClick={() => editPost(post)}><IconEdit size={16} /></ActionIcon>
                        <ActionIcon color="red" onClick={() => { setDeleteTarget(post); openDeleteModal(); }}><IconTrash size={16} /></ActionIcon>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Confirm Deletion">
        <Text>Are you sure you want to delete the blog post <strong>{deleteTarget?.title}</strong>? This action cannot be undone.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={submitting}>Delete</Button>
        </Group>
      </Modal>
    </Container>
  );
};